// A WEBSITE IN AN AI ANSWER — the `site-preview` view (functions/
// previewAnswer.js previewAiViews; the shell mounts it in the assistant's
// thread and on a Fly page with scope { params, embedded }). Read-only:
//
//   head    the site and its page; Inspect, Pick (in inspect), Reload, Open in
//           the preview app (the sheet; ⌘ / Ctrl: a split), the site in a new
//           tab
//   bar     the environments the project has on (env pills — each its own
//           address), the page switcher (the pages the site's data declares),
//           the device (the preview's own device widths)
//   frame   the published site in a sandboxed frame at the device's width,
//           scaled down to the card — or, in INSPECT, the project in the
//           preview's sandboxed user-code frame with element picking (a pick
//           lands in the composer). Until the frame loads: a cover; an
//           environment that is switched off (its address serves nothing) or a
//           site with no pages: that sentence in place of the frame (QA
//           2026-10-18). Never the project's visibility: a private project
//           still serves its site to every visitor.
//
// A published frame is made ONCE per address — its `src` is a plain value of
// a keyed child, so an update never reloads it; a new address (environment,
// page, Reload) is a new child. While reading: one frame-sized stand-in; no
// such site, or a failed read: one sentence (previewAnswerEmptyText) — for a
// name that is a project of the workspace but not a website, or an App Store
// app that is no project here, the sentence says so with its name.
export const SitePreviewView = {
  flow: 'y',
  gap: 'Z',
  minWidth: '0',
  // In a box of fixed height (a Fly page's card) the view fills it and the
  // frame takes the room left under the head and the bar — measured on dev: a
  // 1562×976 frame clipped to a ~115 px strip. In a box of auto height (the
  // assistant's thread) these resolve to nothing and the frame keeps 16:10.
  height: '100%',
  minHeight: '0',
  dataSitePreview: true,
  state: {
    site: null,
    failed: false,
    none: false,
    // The asked name when it is no website (functions/previewAnswer.js
    // previewAnswerResolve): a project of this workspace that is not a
    // website (an app, a module, a library), or an App Store app that is no
    // project here.
    notWebsite: null,
    storeApp: null,
    env: null,
    page: null,
    device: null,
    inspect: false,
    inspectReady: false,
    inspectError: null,
    reload: 0,
    frameReady: null,
    frameSlow: null,
    boxW: 0,
    boxH: 0
  },
  // EMPTY (the views contract, workspace functions/assistantAppViews.js): no
  // such site (or no website at all) — the one sentence is all the view
  // shows, and a Fly page gives its card one row. false with a site (its
  // head, controls and frame — or the sentence that stands in for the frame
  // beside the environments that do serve it); null while reading.
  empty: (el, s) => (s.site ? false : s.failed ? true : null),
  // RESOLVED PARAMS (the views contract): the site, environment and page the
  // card SHOWS — a Fly page's next ask carries them, so a follow-up stays on
  // this site when the model named none. null while reading.
  resolvedParams: (el, s) =>
    s.site
      ? {
          project: s.site.key,
          env: s.site.env,
          ...(s.site.path && s.site.path !== '/' ? { path: s.site.path } : {})
        }
      : null,
  onRender: (el) => el.call('previewAnswerLoad', el),
  // The installed apps decide which projects are websites (a module is not),
  // the project list and the project on screen can land after the view: the
  // read runs again when they change — its own signature keeps it to once per
  // inputs. The card's own choices (env, page) re-run it the same way. The
  // App Store list can land after the view too: when it (or the project list)
  // changes what the asked name means, the read runs again
  // (canvasAnswerNamedMoved).
  subscribeTo: ['workspaceApps', '_projectsForActiveOrg', '_previewParams', '_wsAppsCatalog'],
  onUpdate: (el) => el.call('previewAnswerLoad', el),
  // A picker armed on this card's inspect frame stops with the card.
  onBeforeRemove: (el) => el.call('previewAnswerRemoved', el),

  Skeleton: {
    width: '100%',
    aspectRatio: '16 / 10',
    round: 'radiusControl',
    ariaBusy: 'true',
    hide: (el, s) => !!s.site || !!s.failed
  },

  SitePreviewNote: {
    tag: 'p',
    margin: '0',
    fontSize: 'Z',
    color: 'caption',
    hide: (el, s) => !s.failed,
    text: (el, s) => {
      void s.root.lang
      return el.call('previewAnswerEmptyText', s)
    }
  },

  SitePreviewHead: {
    flow: 'x',
    align: 'center space-between',
    gap: 'Z',
    minWidth: '0',
    hide: (el, s) => !s.site,
    SitePreviewName: {
      tag: 'span',
      minWidth: '0',
      fontSize: 'Z',
      color: 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => {
        const site = s.site
        if (!site) return ''
        return site.path && site.path !== '/' ? site.name + ' · ' + site.path : site.name
      }
    },
    SitePreviewTools: {
      flow: 'x',
      align: 'center',
      gap: 'X',
      flexShrink: '0',
      childExtends: 'CompactIconButton',
      SitePreviewInspect: {
        icon: 'inspect',
        ariaPressed: (el, s) => (s.inspect ? 'true' : 'false'),
        ariaLabel: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.inspect')
        },
        title: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.inspect')
        },
        isOn: (el, s) => !!s.inspect,
        '.isOn': { background: 'selected', color: 'title' },
        onClick: (e, el, s) => {
          e.stopPropagation()
          el.call('previewAnswerInspect', el, !s.inspect)
        }
      },
      SitePreviewPick: {
        icon: 'cursor',
        hide: (el, s) => !s.inspect || !s.inspectReady,
        ariaPressed: (el, s) =>
          s.root.picking && el.call('previewPickArmedOn', el.call('previewAnswerInspectNode', el))
            ? 'true'
            : 'false',
        ariaLabel: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.pick')
        },
        title: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.pick')
        },
        isOn: (el, s) =>
          !!s.root.picking &&
          el.call('previewPickArmedOn', el.call('previewAnswerInspectNode', el)),
        '.isOn': { background: 'selected', color: 'title' },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('previewAnswerPickToggle', el)
        }
      },
      SitePreviewReload: {
        icon: 'reload',
        ariaLabel: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.reload')
        },
        title: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.reload')
        },
        onClick: (e, el) => {
          e.stopPropagation()
          el.call('previewAnswerReload', el)
        }
      },
      SitePreviewOpenApp: {
        icon: 'fullscreen',
        ariaLabel: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.openInPreview')
        },
        title: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.openInPreview')
        },
        onClick: (e, el, s) => {
          e.stopPropagation()
          const site = s.site
          if (!site) return
          el.call('previewAnswerOpenSite', { owner: site.owner, key: site.key, env: site.env }, e)
        }
      },
      // The published site itself, in a new tab — a real link (the browser
      // keeps a click on a `_blank` link; QA 2026-10-18: the old text link
      // was missed).
      SitePreviewOpenTab: {
        extends: ['CompactIconButton', 'Link'],
        icon: 'arrowUpRight',
        href: (el, s) => (s.site ? s.site.url : null),
        target: '_blank',
        rel: 'noopener noreferrer',
        ariaLabel: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.open')
        },
        title: (el, s) => {
          void s.root.lang
          return el.call('polyglot', 'preview.answer.open')
        },
        onClick: (e) => e.stopPropagation()
      }
    }
  },

  SitePreviewBar: {
    flow: 'x',
    align: 'center',
    gap: 'Z',
    flexWrap: 'wrap',
    minWidth: '0',
    hide: (el, s) => !s.site,
    // The environments the project has on — each its own address. The
    // pressed one is the environment the frame shows.
    AppSegmented: {
      compact: true,
      ariaLabel: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.answer.envs')
      },
      hide: (el, s) => !s.site || !Array.isArray(s.site.envs) || s.site.envs.length < 2,
      children: (el, s) =>
        (s.site && Array.isArray(s.site.envs) ? s.site.envs : []).map((k) => ({
          key: 'opt-' + k,
          optionValue: k,
          optionLabel: el.call('previewEnvLabel', k)
        })),
      childProps: {
        isActive: (el, s) => !!s.site && s.site.env === el.optionValue,
        ariaPressed: (el, s) => (!!s.site && s.site.env === el.optionValue ? 'true' : 'false'),
        onClick: (e, el, s) => {
          e.stopPropagation()
          if (!s.site || s.site.env === el.optionValue) return
          s.update({ env: el.optionValue })
        }
      }
    },
    // The pages the site's data declares (and the page on screen).
    InlineSelect_page: {
      hide: (el, s) => !s.site || !Array.isArray(s.site.pages) || s.site.pages.length < 2,
      ariaLabel: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.answer.page')
      },
      value: (el, s) => (s.site ? s.site.path : '/'),
      onClick: (e) => e.stopPropagation(),
      onChange: (ev, el, s) => {
        const next = ev.target.value
        if (!s.site || !next || next === s.site.path) return
        s.update({ page: next })
      },
      childProps: {
        tag: 'option',
        selected: (el, s) => (s.site && el.value === s.site.path ? true : null)
      },
      children: (el, s) => {
        const site = s.site
        const pages = site && Array.isArray(site.pages) ? site.pages.slice() : []
        if (site && site.path && !pages.includes(site.path)) pages.unshift(site.path)
        return pages.map((p, i) => ({ key: 'page-' + i, value: p, text: p }))
      }
    },
    // The device: the site's media queries see its width.
    InlineSelect_device: {
      ariaLabel: (el, s) => {
        void s.root.lang
        return el.call('polyglot', 'preview.answer.deviceLabel')
      },
      value: (el, s) => el.call('previewAnswerDevice', s),
      onClick: (e) => e.stopPropagation(),
      onChange: (ev, el, s) => {
        const next = ev.target.value
        if (next) s.update({ device: next })
      },
      childProps: {
        tag: 'option',
        text: (el, s) => {
          void s.root.lang
          return el.call('polyglot', el.labelKey)
        },
        selected: (el, s) => (el.value === el.call('previewAnswerDevice', s) ? true : null)
      },
      children: () => [
        { key: 'device-desktop', value: 'desktop', labelKey: 'preview.answer.device.desktop' },
        { key: 'device-tablet', value: 'tablet', labelKey: 'preview.answer.device.tablet' },
        { key: 'device-mobile', value: 'mobile', labelKey: 'preview.answer.device.mobile' }
      ]
    }
  },

  // The frame's box: 16:10 of the width, shrinking to the room a fixed-height
  // card leaves (never growing past 16:10). Its size, read on mount and on a
  // resize, scales the device viewport into it.
  SitePreviewFrameBox: {
    position: 'relative',
    overflow: 'hidden',
    width: '100%',
    aspectRatio: '16 / 10',
    flex: '0 1 auto',
    minHeight: '0',
    round: 'radiusControl',
    background: 'overlay',
    hide: (el, s) => !s.site,
    onRender: (el) => el.call('previewAnswerMeasure', el),
    // The box shows once the site is known: its first real size is then.
    stateDeps: [(el, s) => !!s.site],
    onStateUpdate: (el) => el.call('previewAnswerMeasure', el),
    onWindowResize: (e, el) => el.call('previewAnswerMeasure', el),

    SitePreviewFrame: {
      position: 'absolute',
      inset: '0',
      hide: (el, s) => !s.site || !!s.inspect || s.site.serves === 'off',
      children: (el, s) => {
        const site = s.site
        if (!site || !site.url || site.serves === 'off') return []
        // The child's key IS its address (and the Reload count): the same
        // address keeps its frame.
        const frameKey = site.url + '#' + (Number(s.reload) || 0)
        const id = Array.from(frameKey)
          .reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7)
          .toString(36)
        const label = String(el.call('polyglot', 'preview.answer.frameTitle'))
        return [
          {
            extends: 'Iframe',
            key: 'site-' + id,
            frameKey,
            src: site.url,
            title: label.replace('{name}', () => site.name),
            sandbox: 'allow-scripts allow-same-origin allow-forms allow-popups',
            referrerpolicy: 'no-referrer',
            loading: 'lazy',
            display: 'block',
            position: 'absolute',
            top: '0',
            transformOrigin: '0 0',
            border: 'none',
            style: (el, s) => el.call('previewAnswerFrameStyle', s),
            onLoad: (e, el) => el.call('previewAnswerFrameLoaded', el),
            onRender: (el) => el.call('previewAnswerFrameTimer', el),
            onBeforeRemove: (el) => clearTimeout(el.__previewSlowTimer)
          }
        ]
      }
    },

    // INSPECT: the preview's sandboxed user-code frame — the project boots in
    // it on demand (functions/previewAnswer.js previewAnswerInspect).
    SitePreviewInspectFrame: {
      tag: 'iframe',
      position: 'absolute',
      top: '0',
      transformOrigin: '0 0',
      border: 'none',
      display: 'block',
      background: 'white',
      '@dark': { background: 'black' },
      hide: (el, s) => !s.inspect,
      title: (el, s) => {
        void s.root.lang
        return String(el.call('polyglot', 'preview.answer.inspectFrameTitle')).replace(
          '{name}',
          () => (s.site ? s.site.name : '')
        )
      },
      sandbox: (el) => el.call('userCodeSandbox'),
      style: (el, s) => el.call('previewAnswerFrameStyle', s)
    },

    // A sentence IN PLACE of the frame (loading, an environment switched off,
    // no pages, the inspect steps) — the frame's live region.
    SitePreviewCover: {
      position: 'absolute',
      inset: '0',
      flow: 'y',
      align: 'center center',
      padding: 'A',
      background: 'overlay',
      hide: (el, s) => !el.call('previewAnswerNote', s).cover,
      SitePreviewCoverText: {
        tag: 'p',
        role: 'status',
        margin: '0',
        maxWidth: 'G',
        fontSize: 'Z',
        color: 'caption',
        textAlign: 'center',
        text: (el, s) => {
          void s.root.lang
          void s.root.picking
          const note = el.call('previewAnswerNote', s)
          return note.cover ? note.text : ''
        }
      }
    },

    // One quiet line over the frame's foot (a slow site, the picker's hint).
    SitePreviewHint: {
      tag: 'p',
      role: 'status',
      position: 'absolute',
      left: 'Z',
      right: 'Z',
      bottom: 'Z',
      margin: '0',
      padding: 'Y Z',
      round: 'radiusControl',
      theme: 'dialog',
      fontSize: 'Z',
      textAlign: 'center',
      pointerEvents: 'none',
      hide: (el, s) => {
        const note = el.call('previewAnswerNote', s)
        return note.cover || !note.text
      },
      text: (el, s) => {
        void s.root.lang
        void s.root.picking
        const note = el.call('previewAnswerNote', s)
        return note.cover ? '' : note.text
      }
    }
  }
}
